import { MeshBuilder, Vector3, type Mesh } from "@babylonjs/core";
import { HeroId, Slot, ck, fxk, pk } from "@rivalwatch/shared";
import type { Cue } from "../../audio/engine.ts";
import { bell, choir, click, fm, later, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import * as loops from "../../audio/cues/loops.ts";
import type { AvatarClip, ClientHero, ConstructFrame, ProjectileFrame } from "../types.ts";

const PK_TRIDENT = pk(HeroId.SubMarinara, 0);
const PK_WRATH = pk(HeroId.SubMarinara, 1);
const CK_MONSTRO = ck(HeroId.SubMarinara, 0);
const FX_DEEP = fxk(HeroId.SubMarinara, 2);

type Gear = { trident: Mesh; crown: Mesh; cape: Mesh };

const tridentCue: Cue = trim(12, (o) => {
  click(o, 0.25, 4200, 0.004);
  whoosh(o, 0.28, 900, 2800, 0.16);
  bell(o, 900, 0.18, 0.6);
  return 0.38;
});
const wrathCue: Cue = trim(5.5, (o) => {
  thump(o, 0.45, 155, 60, 0.18);
  noise(o, { color: "pink", type: "bandpass", f: 800, f1: 2500, gain: 0.45, a: 0.04, d: 0.34 });
  bell(later(o, 0.08), 740, 0.25, 0.55);
  return 0.65;
});
const blessingCue: Cue = trim(10.8, (o) => {
  whoosh(o, 0.55, 240, 1800, 0.55);
  tone(o, { type: "triangle", f: 170, f1: 480, glide: 0.45, gain: 0.08, a: 0.08, d: 0.5 });
  return 0.9;
});
const monstroCue: Cue = trim(4.3, (o) => {
  thump(o, 0.38, 95, 45, 0.22);
  fm(o, { f: 260, ratio: 1.6, index: 4, index1: 0.4, gain: 0.12, d: 0.38 });
  noise(o, { color: "brown", type: "lowpass", f: 650, gain: 0.35, d: 0.36 });
  return 0.75;
});
const hornCue: Cue = trim(6, (o) => {
  tone(o, { type: "sawtooth", f: 82, f1: 210, glide: 0.7, gain: 0.12, a: 0.08, d: 0.7, lp: 1100 });
  for (const [i, f] of [220, 330, 440].entries()) bell(later(o, 0.18 + i * 0.06), f, 0.55, 0.55);
  noise(o, { color: "brown", type: "lowpass", f: 900, f1: 180, gain: 0.5, d: 0.75 });
  return 1.35;
});

const hero: ClientHero<Gear> = {
  id: HeroId.SubMarinara,
  bulk: 1.05,
  shape: { torso: 1.08, legs: 1.05 },
  gear(g) {
    const { scene, rig, add, glow, toon, dark, R } = g;
    const trident = add(MeshBuilder.CreateCylinder("fork-trident", { diameter: 0.07, height: 1.4, tessellation: 8 }, scene), rig.handR, glow("#72fff1"));
    trident.rotation.x = Math.PI / 2;
    trident.position.z = -0.55;
    for (const x of [-0.12, 0, 0.12]) {
      const tine = add(MeshBuilder.CreateCylinder("trident-tine", { diameter: 0.035, height: 0.35, tessellation: 6 }, scene), rig.handR, glow("#72fff1"));
      tine.rotation.x = Math.PI / 2;
      tine.position.set(x, 0, -1.25);
    }
    for (const side of [-1, 1]) {
      const ear = add(MeshBuilder.CreateCylinder("pointed-ear", { diameterTop: 0, diameterBottom: 0.16, height: 0.32, tessellation: 3 }, scene), rig.head, toon("#d2936c"));
      ear.rotation.z = side * 0.9;
      ear.position.set(side * 0.3, 0.02, -0.02);
    }
    const crown = add(MeshBuilder.CreateTorus("pasta-crown", { diameter: 0.46, thickness: 0.035, tessellation: 24 }, scene), rig.head, glow("#ffd36e"));
    crown.position.y = 0.25;
    const cape = add(MeshBuilder.CreateBox("kelp-cape", { width: R * 1.4, height: 1.0, depth: 0.05 }, scene), rig.torso, toon("#0e6f74"));
    cape.position.set(0, -0.1, 0.34);
    const belt = add(MeshBuilder.CreateTorus("noodle-belt", { diameter: 0.78, thickness: 0.035, tessellation: 24 }, scene), rig.torso, dark);
    belt.rotation.x = Math.PI / 2;
    belt.position.y = -0.35;
    return { trident, crown, cape };
  },
  animate(gear, f): AvatarClip {
    gear.crown.rotation.y += f.dt * (f.ultActive ? 6 : 1);
    gear.cape.rotation.x = 0.1 + Math.sin(f.t * 4) * 0.05;
    return f.action === 2 ? "holding-right-shoot" : "holding-right";
  },
  projectiles: {
    [PK_TRIDENT](scene, f: ProjectileFrame) {
      const spear = MeshBuilder.CreateCylinder("trident-shot", { diameter: 0.08, height: 0.8, tessellation: 8 }, scene);
      const update = (_: unknown, p: ProjectileFrame) => {
        spear.position.set(p.pos.x, p.pos.y, p.pos.z);
        spear.lookAt(new Vector3(p.pos.x + p.dir.x, p.pos.y + p.dir.y, p.pos.z + p.dir.z));
        spear.rotation.x += Math.PI / 2;
      };
      update(null, f);
      return { gear: spear, update, dispose: () => spear.dispose() };
    },
    [PK_WRATH](scene, f: ProjectileFrame) {
      const orb = MeshBuilder.CreateSphere("sauce-wrath", { diameter: 0.45, segments: 12 }, scene);
      const update = (_: unknown, p: ProjectileFrame) => orb.position.set(p.pos.x, p.pos.y, p.pos.z);
      update(null, f);
      return { gear: orb, update, dispose: () => orb.dispose() };
    },
  },
  constructs: {
    [CK_MONSTRO](scene, frame: ConstructFrame) {
      const body = MeshBuilder.CreateSphere("monstro-body", { diameter: 1.15, segments: 12 }, scene);
      const eye = MeshBuilder.CreateSphere("monstro-eye", { diameter: 0.2, segments: 8 }, scene);
      eye.parent = body;
      eye.position.set(0, 0.2, -0.5);
      const fin = MeshBuilder.CreateCylinder("monstro-fin", { diameterTop: 0, diameterBottom: 0.35, height: 0.45, tessellation: 3 }, scene);
      fin.parent = body;
      fin.rotation.z = Math.PI / 2;
      fin.position.y = 0.45;
      for (const side of [-1, 1]) {
        const flipper = MeshBuilder.CreateCylinder("monstro-flipper", { diameterTop: 0, diameterBottom: 0.22, height: 0.35, tessellation: 3 }, scene);
        flipper.parent = body;
        flipper.rotation.z = side * 0.7;
        flipper.position.set(side * 0.45, -0.05, 0);
      }
      const update = (_: unknown, f: ConstructFrame) => {
        body.position.set(f.pos.x, f.pos.y + 0.65, f.pos.z);
        body.rotation.y = f.yaw + Math.sin(f.ticks * 0.05) * 0.2;
      };
      update(null, frame);
      return { gear: { body, eye }, update, dispose: () => { body.dispose(); eye.dispose(); } };
    },
  },
  fx: { [FX_DEEP]: { keepGeneric: true } },
  audio: {
    weight: "mid",
    fire: () => ({ key: "fire:sub-marinara:trident", cue: tridentCue }),
    ability: {
      [Slot.Secondary]: wrathCue,
      [Slot.Ability1]: blessingCue,
      [Slot.Ability2]: monstroCue,
      [Slot.Ultimate]: hornCue,
    },
    theme: { bpm: 104, octave: -1, voice: choir, notes: [[0, 0.5], [5, 0.5], [7, 0.5], [12, 1]] },
    themeTrim: { heroic: 2.5, hostile: -6, tease: 4 },
    constructLoops: { [CK_MONSTRO]: { build: loops.amplifier, gain: 0.45, range: 32 } },
  },
  icons: {
    [Slot.Primary]: "M12 3v18M8 6l4-3 4 3M8 11h8",
    [Slot.Secondary]: "M5 14c4-6 10-6 14 0M8 17h8",
    [Slot.Ability1]: "M12 20c-4-4-4-9 0-14c4 5 4 10 0 14z",
    [Slot.Ability2]: "M6 15c2-5 10-5 12 0M9 12h6M12 9v8",
    [Slot.Ultimate]: "M4 17c4-9 12-9 16 0M8 18h8M12 4v5",
    passive: "M6 15c3 4 9 4 12 0",
  },
};

export default hero;
